import { useEffect, useRef, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { comprimirImagen, esImagen, ACCEPT_IMAGEN } from "@/lib/comprimirImagen";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RepuestoPickerDialog } from "@/components/RepuestoPickerDialog";
import { Camera, Video, Trash2, PackagePlus, X, AlertCircle, CheckCircle2 } from "lucide-react";

const LADO_MAYOR_FOTO = 1600, CALIDAD_FOTO = 0.8;
const PEDIDO_ESTADO_LABEL = {
  pendiente: "En cola para Honda", creando: "Enviando a Honda...", en_proceso: "Enviando a Honda...",
  creada: "Enviado a Honda", incierto: "Verificar en Honda", bloqueada: "Bloqueado en Honda", error: "Error",
  simulado: "Prueba: cargado sin enviar",
};
const PEDIDO_ESTADO_CLS = {
  pendiente: "text-slate-500", creando: "text-indigo-600", en_proceso: "text-indigo-600",
  creada: "text-emerald-600", incierto: "text-amber-600", bloqueada: "text-rose-600", error: "text-rose-600",
  simulado: "text-amber-600",
};
const PEDIDO_ACTIVOS = ["pendiente", "creando", "en_proceso"];

// Igual que BlobImg de FotosTrabajo: las URLs firmadas de Spaces son absolutas y se usan
// directo; la ruta local de respaldo es relativa y necesita el token, así que se trae como blob.
const BlobMedia = ({ src, tipo, className }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revocar = null;
    let activo = true;
    if (!src) { setUrl(null); return undefined; }
    if (src.startsWith("http")) { setUrl(src); return undefined; }
    api.get(src, { responseType: "blob" }).then((r) => {
      if (!activo) return;
      revocar = URL.createObjectURL(r.data);
      setUrl(revocar);
    }).catch(() => {});
    return () => { activo = false; if (revocar) URL.revokeObjectURL(revocar); };
  }, [src]);
  if (!url) return <div className={`${className} bg-slate-100 animate-pulse`} />;
  return tipo === "video"
    ? <video src={url} controls className={className} />
    : <img src={url} alt="" className={className} />;
};

// Fase B del reporte de garantía Honda: diagnóstico, repuestos, síntomas y fotos/video que
// el robot cargará luego en la orden de garantía (pedido 3, aparte de esta pantalla).
export function GarantiaReporteDialog({ trabajoId, onClose }) {
  const [cargando, setCargando] = useState(false);
  const [reporteCliente, setReporteCliente] = useState("");
  const [diagnostico, setDiagnostico] = useState("");
  const [repuestos, setRepuestos] = useState([]);
  const [repuestoFallaCodigo, setRepuestoFallaCodigo] = useState("");
  const [sintoma, setSintoma] = useState("");
  const [detalle, setDetalle] = useState("");
  const [detalleFinal, setDetalleFinal] = useState("");
  const [sintomasCatalogo, setSintomasCatalogo] = useState([]);
  const [modeloId, setModeloId] = useState(null);
  const [modeloNombre, setModeloNombre] = useState("");
  const [archivos, setArchivos] = useState([]);
  const [faltan, setFaltan] = useState([]);
  const [completo, setCompleto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [subiendoVideo, setSubiendoVideo] = useState(false);
  const [pedidoHonda, setPedidoHonda] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const inputFotoRef = useRef(null);
  const inputVideoRef = useRef(null);

  const cargarArchivos = useCallback(async () => {
    if (!trabajoId) return;
    const { data } = await api.get(`/trabajos/${trabajoId}/garantia/archivos`);
    setArchivos(data);
  }, [trabajoId]);

  const cargarCompleto = useCallback(async () => {
    if (!trabajoId) return;
    const { data } = await api.get(`/trabajos/${trabajoId}/garantia/completo`);
    setFaltan(data.faltan); setCompleto(data.completo);
  }, [trabajoId]);

  const cargarPedido = useCallback(async () => {
    if (!trabajoId) return;
    const { data } = await api.get(`/trabajos/${trabajoId}/honda-orden`, { params: { tipo: "garantia_pedido" } });
    setPedidoHonda(data && data.id ? data : null);
  }, [trabajoId]);

  useEffect(() => {
    if (!trabajoId) return;
    setCargando(true);
    (async () => {
      try {
        const [g, sint, trab] = await Promise.all([
          api.get(`/trabajos/${trabajoId}/garantia`),
          api.get("/honda-sintomas"),
          api.get(`/trabajos/${trabajoId}`),
        ]);
        setReporteCliente(g.data.reporte_cliente || "");
        setDiagnostico(g.data.diagnostico || "");
        setRepuestos((g.data.repuestos || []).map((r) => ({ ...r })));
        setRepuestoFallaCodigo(g.data.repuesto_falla_codigo || "");
        setSintoma(g.data.sintoma || "");
        setDetalle(g.data.detalle || "");
        setDetalleFinal(g.data.detalle_final || "");
        setSintomasCatalogo(sint.data.sintomas || []);
        setModeloId(trab.data.vehiculo?.modelo_id || null);
        setModeloNombre(trab.data.vehiculo?.modelo || "");
        await Promise.all([cargarArchivos(), cargarCompleto(), cargarPedido()]);
      } catch (e) { toast.error(errorMsg(e)); }
      finally { setCargando(false); }
    })();
  }, [trabajoId, cargarArchivos, cargarCompleto, cargarPedido]);

  // Mientras haya un video "procesando", refrescar el listado cada 5s.
  useEffect(() => {
    if (!trabajoId) return undefined;
    if (!archivos.some((a) => a.estado === "procesando")) return undefined;
    const t = setInterval(() => { cargarArchivos(); cargarCompleto(); }, 5000);
    return () => clearInterval(t);
  }, [trabajoId, archivos, cargarArchivos, cargarCompleto]);

  // Mientras el envío a Honda esté en curso, refrescar su estado cada 5s.
  useEffect(() => {
    if (!trabajoId || !pedidoHonda) return undefined;
    if (!PEDIDO_ACTIVOS.includes(pedidoHonda.estado)) return undefined;
    const t = setInterval(() => { cargarPedido(); }, 5000);
    return () => clearInterval(t);
  }, [trabajoId, pedidoHonda, cargarPedido]);

  if (!trabajoId) return null;

  const sintomaObj = sintomasCatalogo.find((s) => s.texto === sintoma) || null;
  const detalleObj = (sintomaObj?.detalles || []).find((d) => d.texto === detalle) || null;
  const finales = detalleObj?.finales || [];

  const agregarRepuestos = (sel) => {
    const sinCodigo = sel.filter((r) => !(r.codigo || "").trim());
    const conCodigo = sel.filter((r) => (r.codigo || "").trim());
    if (sinCodigo.length) {
      toast.error(`Sin código de parte, no se agregaron: ${sinCodigo.map((r) => r.nombre).join(", ")}`);
    }
    const nuevos = conCodigo.map((r) => ({ repuesto_id: r.ref_id, codigo_parte: r.codigo, nombre: r.nombre, cantidad: r.cantidad || 1 }));
    setRepuestos(nuevos);
    if (!nuevos.length) { setRepuestoFallaCodigo(""); return; }
    if (!nuevos.some((r) => r.codigo_parte === repuestoFallaCodigo)) {
      setRepuestoFallaCodigo(nuevos[0].codigo_parte);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/trabajos/${trabajoId}/garantia`, {
        diagnostico, repuestos, repuesto_falla_codigo: repuestoFallaCodigo || null,
        sintoma: sintoma || null, detalle: detalle || null, detalle_final: detalleFinal || null,
      });
      toast.success("Reporte de garantía guardado");
      await cargarCompleto();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  const subirFotos = async (files) => {
    if (!files.length) return;
    setSubiendoFoto(true);
    try {
      for (const file of files) { // eslint-disable-line no-await-in-loop
        if (!esImagen(file)) { toast.error("Elija una imagen"); continue; } // eslint-disable-line no-await-in-loop
        const blob = await comprimirImagen(file, LADO_MAYOR_FOTO, CALIDAD_FOTO); // eslint-disable-line no-await-in-loop
        const fd = new FormData();
        fd.append("archivo", blob, "foto.jpg");
        fd.append("tipo", "foto");
        await api.post(`/trabajos/${trabajoId}/garantia/archivos`, fd); // eslint-disable-line no-await-in-loop
      }
      await Promise.all([cargarArchivos(), cargarCompleto()]);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setSubiendoFoto(false); }
  };

  const subirVideo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Elija un video"); return; }
    setSubiendoVideo(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("tipo", "video");
      await api.post(`/trabajos/${trabajoId}/garantia/archivos`, fd);
      toast.success("Video en proceso, puede tardar unos minutos");
      await Promise.all([cargarArchivos(), cargarCompleto()]);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setSubiendoVideo(false); }
  };

  const eliminarArchivo = async (archivo) => {
    if (!window.confirm(`¿Eliminar este ${archivo.tipo === "video" ? "video" : "foto"}?`)) return;
    try {
      await api.delete(`/garantia-archivos/${archivo.id}`);
      await Promise.all([cargarArchivos(), cargarCompleto()]);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const enviarAHonda = async () => {
    setEnviando(true);
    try {
      await api.post(`/trabajos/${trabajoId}/garantia/enviar-honda`);
      toast.success("Enviado a la cola para Honda");
      await cargarPedido();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setEnviando(false); }
  };

  return (
    <Dialog open={!!trabajoId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="garantia-reporte-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reporte de garantía Honda</DialogTitle>
        </DialogHeader>

        {cargando ? (
          <p className="text-sm text-slate-500 py-4" data-testid="garantia-reporte-cargando">Cargando…</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Reporte del cliente</label>
              <Textarea data-testid="garantia-reporte-cliente-lectura" value={reporteCliente} readOnly rows={2}
                className="bg-slate-50 text-slate-600" />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Diagnóstico del mecánico</label>
              <Textarea data-testid="garantia-diagnostico-input" rows={3} value={diagnostico}
                onChange={(e) => setDiagnostico(e.target.value)} placeholder="Diagnóstico interno (no se envía a Honda)" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Repuestos</label>
                <Button type="button" size="sm" variant="outline" data-testid="garantia-repuestos-agregar-btn"
                  onClick={() => setPickerOpen(true)} className="border-indigo-300 text-indigo-600 hover:bg-indigo-50">
                  <PackagePlus size={14} className="mr-1.5" /> Elegir repuestos
                </Button>
              </div>
              {repuestos.length === 0 ? (
                <p className="text-sm text-slate-400 py-2" data-testid="garantia-repuestos-vacio">Sin repuestos agregados</p>
              ) : (
                <div className="space-y-1.5">
                  {repuestos.map((r) => (
                    <div key={r.codigo_parte} data-testid={`garantia-repuesto-item-${r.codigo_parte}`}
                      className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5">
                      <label className="flex items-center gap-2 shrink-0 cursor-pointer" title="Repuesto que generó el fallo">
                        <input type="radio" data-testid={`garantia-repuesto-falla-${r.codigo_parte}`}
                          name="garantia-repuesto-falla" checked={repuestoFallaCodigo === r.codigo_parte}
                          onChange={() => setRepuestoFallaCodigo(r.codigo_parte)} className="accent-indigo-600 w-4 h-4" />
                      </label>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{r.nombre}</p>
                        <p className="text-xs text-slate-400 font-mono">{r.codigo_parte || "—"}</p>
                      </div>
                      <span className="text-xs text-slate-500">x{r.cantidad}</span>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400">Marque con el radio cuál repuesto generó el fallo.</p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Síntomas</label>
              {sintomasCatalogo.length === 0 ? (
                <p className="text-sm text-amber-600" data-testid="garantia-sintomas-sin-catalogo">
                  Catálogo de síntomas pendiente de cargar
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Select value={sintoma} onValueChange={(v) => { setSintoma(v); setDetalle(""); setDetalleFinal(""); }}>
                    <SelectTrigger data-testid="garantia-sintoma-select"><SelectValue placeholder="Síntoma" /></SelectTrigger>
                    <SelectContent>
                      {sintomasCatalogo.map((s) => <SelectItem key={s.texto} value={s.texto}>{s.texto}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={detalle} onValueChange={(v) => { setDetalle(v); setDetalleFinal(""); }} disabled={!sintomaObj}>
                    <SelectTrigger data-testid="garantia-detalle-select"><SelectValue placeholder="Detalle" /></SelectTrigger>
                    <SelectContent>
                      {(sintomaObj?.detalles || []).map((d) => <SelectItem key={d.texto} value={d.texto}>{d.texto}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {finales.length > 0 && (
                    <Select value={detalleFinal} onValueChange={setDetalleFinal}>
                      <SelectTrigger data-testid="garantia-detalle-final-select"><SelectValue placeholder="Detalle final" /></SelectTrigger>
                      <SelectContent>
                        {finales.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">Fotos y video</label>
                <Button type="button" size="sm" variant="outline" data-testid="garantia-fotos-agregar-btn"
                  disabled={subiendoFoto} onClick={() => inputFotoRef.current?.click()}
                  className="border-slate-300 text-slate-600 hover:bg-slate-50">
                  <Camera size={14} className="mr-1.5" /> {subiendoFoto ? "Subiendo..." : "Agregar fotos"}
                </Button>
                <Button type="button" size="sm" variant="outline" data-testid="garantia-video-agregar-btn"
                  disabled={subiendoVideo} onClick={() => inputVideoRef.current?.click()}
                  className="border-slate-300 text-slate-600 hover:bg-slate-50">
                  <Video size={14} className="mr-1.5" /> {subiendoVideo ? "Subiendo..." : "Agregar video"}
                </Button>
                <input ref={inputFotoRef} type="file" accept={ACCEPT_IMAGEN} multiple className="hidden"
                  data-testid="garantia-fotos-input"
                  onChange={(e) => { subirFotos(Array.from(e.target.files || [])); e.target.value = ""; }} />
                <input ref={inputVideoRef} type="file" accept="video/*" className="hidden"
                  data-testid="garantia-video-input"
                  onChange={(e) => { subirVideo(e.target.files?.[0]); e.target.value = ""; }} />
              </div>
              {archivos.length === 0 ? (
                <p className="text-sm text-slate-400 py-2" data-testid="garantia-archivos-vacio">Sin fotos ni video agregados</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {archivos.map((a) => (
                    <div key={a.id} data-testid={`garantia-archivo-${a.id}`} className="space-y-1">
                      <div className="relative w-full aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-900">
                        {a.estado === "listo" ? (
                          <BlobMedia src={a.fotos_url} tipo={a.tipo} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {a.tipo === "video" ? <Video size={22} className="text-slate-300" /> : <Camera size={22} className="text-slate-300" />}
                          </div>
                        )}
                        <button type="button" title="Eliminar" data-testid={`garantia-archivo-eliminar-${a.id}`}
                          onClick={() => eliminarArchivo(a)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <p className={`text-[11px] text-center truncate ${
                        a.estado === "listo" ? "text-emerald-600" : a.estado === "error" ? "text-pink-600" : "text-amber-600"}`}
                        data-testid={`garantia-archivo-estado-${a.id}`} title={a.mensaje || ""}>
                        {a.estado === "listo" ? "Listo" : a.estado === "error" ? `Error: ${a.mensaje || ""}` : "Procesando…"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div className="flex-1 min-w-0" data-testid="garantia-faltan-lista">
                {completo ? (
                  subiendoFoto || subiendoVideo ? (
                    <p className="flex items-center gap-1.5 text-sm text-amber-600">
                      <AlertCircle size={14} /> Espere a que termine de subir el archivo
                    </p>
                  ) : (
                    <p className="flex items-center gap-1.5 text-sm text-emerald-600">
                      <CheckCircle2 size={15} /> Reporte completo
                    </p>
                  )
                ) : (
                  <div className="flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>Falta: {faltan.join(", ")}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" data-testid="garantia-cerrar-btn" onClick={onClose}>
                  <X size={14} className="mr-1.5" /> Cerrar
                </Button>
                <Button data-testid="garantia-guardar-btn" onClick={guardar} disabled={guardando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div className="flex-1 min-w-0" data-testid="garantia-pedido-estado">
                {pedidoHonda ? (
                  <p className={`text-sm ${PEDIDO_ESTADO_CLS[pedidoHonda.estado] || "text-slate-500"}`}>
                    {PEDIDO_ESTADO_LABEL[pedidoHonda.estado] || pedidoHonda.estado}
                    {pedidoHonda.mensaje && <span className="block text-xs text-slate-500">{pedidoHonda.mensaje}</span>}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">Todavía no se envió el pedido a Honda.</p>
                )}
              </div>
              <Button data-testid="garantia-enviar-honda-btn" onClick={enviarAHonda}
                disabled={!completo || enviando || subiendoFoto || subiendoVideo ||
                  (pedidoHonda && PEDIDO_ACTIVOS.includes(pedidoHonda.estado)) || pedidoHonda?.estado === "creada"}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
                {enviando ? "Enviando..." : "Enviar a Honda"}
              </Button>
            </div>
          </div>
        )}

        <RepuestoPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} modeloId={modeloId} modeloNombre={modeloNombre}
          tareaNombre="Reporte de garantía" seleccion={repuestos.map((r) => ({ ref_id: r.repuesto_id || r.codigo_parte,
            codigo: r.codigo_parte, nombre: r.nombre, cantidad: r.cantidad }))}
          onConfirmar={agregarRepuestos} />
      </DialogContent>
    </Dialog>
  );
}
